/**
 * Riley's staged-draft fingerprint is `sha256(normalised text)` (riley_artifacts.fingerprint;
 * Desk normalises HTML, so comparison is on text). The composer sends it only when the text is
 * still Riley's draft, so the server can choose `sendDraft`; the server re-checks it.
 * Owner: web-case-composer (slice 16).
 */
import { textOf } from '../case-view/api/text'

export const normaliseText = (html: string) => textOf(html).replace(/\s+/g, ' ').trim()

export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

/** The fingerprint to send, or null when the user changed Riley's draft (or there was none). */
export async function draftFingerprint(html: string, rileyDraftHtml: string | undefined): Promise<string | null> {
  if (!rileyDraftHtml) return null
  const now = normaliseText(html)
  if (!now || now !== normaliseText(rileyDraftHtml)) return null
  return sha256Hex(now)
}

/** Strip ProseMirror's view-only markup from the live DOM so the compose-phase guard sees clean HTML. */
export function cleanEditorHtml(html: string): string {
  return html
    .replace(/<br class="ProseMirror-trailingBreak">/g, '')
    .replace(/\s(?:class|contenteditable|draggable)="[^"]*"/g, '')
    .replace(/<p><\/p>/g, '')
}

export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const b = crypto.getRandomValues(new Uint8Array(16))
  b[6] = ((b[6] as number) & 0x0f) | 0x40
  b[8] = ((b[8] as number) & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}
